import type { AppDispatch } from '@/config/store/store'
import {
  campaignsApi,
  type FetchCampaignsQueryParams
} from '@/ducks/campaigns/api/campaigns.api'
import type {
  BuyXGetYCampaign,
  Campaign,
  CampaignLimit,
  CampaignStatus,
  CartDiscountCampaign,
  FoodOfferCampaign
} from '@/ducks/campaigns/campaigns.types'
import { DEFAULT_CAMPAIGN_LIMIT } from '@/ducks/campaigns/campaigns.types'
import { getCartDiscountId } from '@/ducks/campaigns/cartDiscountCampaign.helpers'
import { getFoodOfferFoodId } from '@/ducks/campaigns/foodOfferCampaign.helpers'
import type { z } from 'zod'
import type { ApiCartDiscountSchema } from '@/ducks/offers/offers.schema'
import { rootApi } from '@/ducks/root.api'
import type { RootState } from '@/ducks/root.reducer'

type CartDiscount = z.infer<typeof ApiCartDiscountSchema>[number]

const patchMatchingCampaigns = (
  dispatch: AppDispatch,
  getState: () => RootState,
  match: (campaign: Campaign) => boolean,
  update: (campaign: Campaign) => void
) => {
  const cachedArgs = campaignsApi.util.selectCachedArgsForQuery(
    getState(),
    'fetchCampaigns'
  )

  for (const args of cachedArgs) {
    dispatch(
      campaignsApi.util.updateQueryData('fetchCampaigns', args, draft => {
        for (const campaign of draft.campaigns) {
          if (match(campaign)) {
            update(campaign)
          }
        }
      })
    )
  }
}

export const patchCampaignStatusInCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  campaignId: string,
  status: CampaignStatus
) => {
  const cachedArgs = campaignsApi.util.selectCachedArgsForQuery(
    getState(),
    'fetchCampaigns'
  )

  for (const args of cachedArgs) {
    dispatch(
      campaignsApi.util.updateQueryData('fetchCampaigns', args, draft => {
        const campaign = draft.campaigns.find(c => c.campaignId === campaignId)
        if (campaign) {
          campaign.status = status
        }
      })
    )
  }
}

export const patchFoodOfferCampaignInCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  foodId: string,
  patch: Pick<FoodOfferCampaign, 'offerAmount' | 'offerValidity' | 'validUntil'>
) => {
  patchMatchingCampaigns(
    dispatch,
    getState,
    campaign =>
      campaign.campaignType === 'food-offer' &&
      (campaign.campaignId === foodId ||
        getFoodOfferFoodId(campaign) === foodId),
    campaign => {
      if (campaign.campaignType === 'food-offer') {
        Object.assign(campaign, patch)
      }
    }
  )
}

export const patchFoodOfferCampaignStatusInCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  foodId: string,
  status: CampaignStatus
) => {
  patchMatchingCampaigns(
    dispatch,
    getState,
    campaign =>
      campaign.campaignType === 'food-offer' &&
      (campaign.campaignId === foodId ||
        getFoodOfferFoodId(campaign) === foodId),
    campaign => {
      campaign.status = status
    }
  )
}

export const removeCampaignFromCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  match: (campaign: Campaign) => boolean,
  preferredArgs?: FetchCampaignsQueryParams
): number => {
  const cachedArgs = campaignsApi.util.selectCachedArgsForQuery(
    getState(),
    'fetchCampaigns'
  )

  const argsToUpdate: FetchCampaignsQueryParams[] = []
  const seen = new Set<string>()

  const addArgs = (args: FetchCampaignsQueryParams) => {
    const key = JSON.stringify(args)
    if (seen.has(key)) return
    seen.add(key)
    argsToUpdate.push(args)
  }

  if (preferredArgs) addArgs(preferredArgs)
  for (const args of cachedArgs) addArgs(args)

  let totalRemoved = 0

  for (const args of argsToUpdate) {
    dispatch(
      campaignsApi.util.updateQueryData('fetchCampaigns', args, draft => {
        const removedCount = draft.campaigns.filter(match).length
        if (removedCount === 0) return

        draft.campaigns = draft.campaigns.filter(campaign => !match(campaign))
        draft.total = Math.max(0, draft.total - removedCount)
        totalRemoved += removedCount
      })
    )
  }

  return totalRemoved
}

export const removeFoodOfferCampaignFromCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  foodId: string
) => {
  removeCampaignFromCache(
    dispatch,
    getState,
    campaign =>
      campaign.campaignType === 'food-offer' &&
      (campaign.campaignId === foodId ||
        getFoodOfferFoodId(campaign) === foodId)
  )
}

export type CampaignsCacheSnapshot = {
  total: number
  campaignIds: string[]
}

export const getCampaignsCacheSnapshot = (
  getState: () => RootState,
  preferredLimit: CampaignLimit = DEFAULT_CAMPAIGN_LIMIT
): CampaignsCacheSnapshot => {
  const preferredArgs = { page: 0, limit: preferredLimit }
  const cachedData = campaignsApi.endpoints.fetchCampaigns.select(preferredArgs)(
    getState()
  )

  return {
    total: cachedData?.data?.total ?? 0,
    campaignIds:
      cachedData?.data?.campaigns?.map(campaign => campaign.campaignId) ?? []
  }
}

let stashedPostCreateCampaignsBaseline: CampaignsCacheSnapshot | null = null

export const stashPostCreateCampaignsBaseline = (
  getState: () => RootState
): CampaignsCacheSnapshot => {
  stashedPostCreateCampaignsBaseline = getCampaignsCacheSnapshot(getState)
  return stashedPostCreateCampaignsBaseline
}

export const consumeStashedPostCreateCampaignsBaseline =
  (): CampaignsCacheSnapshot | null => {
    const baseline = stashedPostCreateCampaignsBaseline
    stashedPostCreateCampaignsBaseline = null
    return baseline
  }

export const refreshCampaignsCache = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  preferredLimit: CampaignLimit = DEFAULT_CAMPAIGN_LIMIT
): Promise<CampaignsCacheSnapshot> => {
  const cachedArgs = campaignsApi.util.selectCachedArgsForQuery(
    getState(),
    'fetchCampaigns'
  )

  const preferredArgs = { page: 0, limit: preferredLimit }
  const argsToRefetch = [
    ...cachedArgs.filter(
      args => args.page !== preferredArgs.page || args.limit !== preferredArgs.limit
    ),
    preferredArgs
  ]

  await Promise.all(
    argsToRefetch.map(args =>
      dispatch(
        campaignsApi.endpoints.fetchCampaigns.initiate(args, {
          forceRefetch: true,
          subscribe: false
        })
      ).unwrap()
    )
  )

  const freshData = campaignsApi.endpoints.fetchCampaigns.select(preferredArgs)(
    getState()
  )

  return {
    total: freshData?.data?.total ?? 0,
    campaignIds:
      freshData?.data?.campaigns?.map(campaign => campaign.campaignId) ?? []
  }
}

export const patchBuyXGetYCampaignInCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  offerId: string,
  patch: Partial<
    Pick<
      BuyXGetYCampaign,
      | 'nbrItemsPurchased'
      | 'nbrItemsOffer'
      | 'itemToBuy'
      | 'itemToGet'
      | 'expiryDate'
      | 'name'
    >
  >
) => {
  patchMatchingCampaigns(
    dispatch,
    getState,
    campaign =>
      campaign.campaignType === 'buyXGetYOffer' &&
      campaign.campaignId === offerId,
    campaign => {
      if (campaign.campaignType === 'buyXGetYOffer') {
        Object.assign(campaign, patch)
      }
    }
  )
}

export const patchCartDiscountCampaignInCache = (
  dispatch: AppDispatch,
  getState: () => RootState,
  cartId: string,
  patch: Pick<
    CartDiscountCampaign,
    | 'amountPercentage'
    | 'maxOff'
    | 'targetAmount'
    | 'expirationTime'
    | 'discountType'
  >,
  cartDiscounts?: CartDiscount[]
) => {
  patchMatchingCampaigns(
    dispatch,
    getState,
    campaign => {
      if (campaign.campaignType !== 'cart_discount') return false
      if (campaign.campaignId === cartId) return true
      return getCartDiscountId(campaign, cartDiscounts) === cartId
    },
    campaign => {
      if (campaign.campaignType === 'cart_discount') {
        Object.assign(campaign, patch)
      }
    }
  )
}
